import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BuildIcon from '@mui/icons-material/Build'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material'
import { ReactNode, useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useGlobalApproverSettings } from 'api/aws/approvers/fetchGlobalApprovers'
import { useUserItems } from 'api/aws/users/useUserItems'
import { SEARCH_HEALTH_KEY, triggerSearchRefresh } from 'api/search/useSearchHealth'
import { useActiveUser } from 'api/slack/useActiveUser'
import SearchHealthBadge from 'components/Health/SearchHealthBadge'
import GoogleSignatureModal from 'components/Team/GoogleSignatureModal'
import { useGoogleSignature } from 'components/Team/useGoogleSignature'
import ApproverSettingsModal from 'context/Settings/ApproverSettingsModal'
import { INITIAL_STATE } from 'context/Settings/types'
import { useApproverSettings } from 'context/Settings/useApproverSettings'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useLiveSessionDuration, useTimeTracker } from 'context/TimeTracker'
import GmailIconRed from '../../../images/icons/gmail-icon-red.png'

interface DevMenuItemProps {
  title: string
  icon: ReactNode
  color: string
  onClick: () => void
  disabled?: boolean
  secondary?: string
}

const DevMenuItem = ({ title, icon, color, onClick, disabled, secondary }: DevMenuItemProps) => {
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  return (
    <ListItem
      button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: '6px',
        filter: 'grayscale(100%)',
        transition: 'background-color 0.3s, border-color 0.3s ease',
        border: '1px solid transparent',
        '&:hover': {
          filter: 'none',
          transition: 'filter 0.2s ease-in-out',
          backgroundColor: 'transparent',
          border: `1px solid ${color}`,
          '& .MuiSvgIcon-root': {
            color: color,
          },
          '& .dev-menu-icon': {
            filter: 'none',
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 40,
          color: isHovered ? color : 'inherit',
          transition: 'color 0.2s ease-in-out',
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={title}
        secondary={secondary}
        sx={{ textAlign: 'left', color: theme.palette.text.primary }}
      />
    </ListItem>
  )
}

const DevNavMenu = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const { data: globalApprovers } = useGlobalApproverSettings(true)
  const settings = useApproverSettings(globalApprovers ?? INITIAL_STATE)
  const { handleOpenApproversSettings, handleCloseApproversSettings, isApproverSettingsOpen } = settings
  const theme = useTheme()

  // Time tracker
  const { isWidgetVisible, toggleWidget, isTracking } = useTimeTracker()
  const { formattedDuration } = useLiveSessionDuration()

  // Google Signature
  const { data: users } = useUserItems()
  const { activeUser } = useActiveUser()
  const currentUser = users?.find(user => activeUser?.user?.id === user?.user_uuid)
  const accessToken = localStorage.getItem('google_access_token')
  const [isSignatureOpen, setIsSignatureOpen] = useState(false)
  const [signatureStatus, setSignatureStatus] = useState<string | null>(null)

  const generateSignature = useGoogleSignature(currentUser, activeUser, 'gmail')
  const generateRenderSignature = useGoogleSignature(currentUser, activeUser, 'render')

  const handleSetSignature = async () => {
    const signature = generateSignature()
    const encodedEmail = encodeURIComponent(currentUser?.email)
    const url = `https://gmail.googleapis.com/gmail/v1/users/${encodedEmail}/settings/sendAs/${encodedEmail}`
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signature }),
      })
      if (!response.ok) throw new Error(`Status ${response.status}`)
      setSignatureStatus('✅ Signature successfully updated in Gmail!')
    } catch (error) {
      console.error('Signature update failed:', error)
      setSignatureStatus('❌ Failed to update signature. Check your permissions or try again.')
    }
  }

  // Search refresh
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbarContext()
  const { mutate: refreshSearch, isPending: isRefreshing } = useMutation({
    mutationFn: triggerSearchRefresh,
    onSuccess: result => {
      if (result.success) {
        showSnackbar(result.message || 'Search indexes refreshed', 'success')
        queryClient.invalidateQueries({ queryKey: SEARCH_HEALTH_KEY })
      } else {
        showSnackbar(result.error || 'Refresh failed', 'error')
      }
    },
    onError: (error: any) => {
      showSnackbar(error.message || 'Refresh failed', 'error')
    },
  })

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setIsMenuOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsMenuOpen(false)
  }

  const open = Boolean(anchorEl)

  return (
    <Box>
      <Tooltip title="Dev Tools">
        <IconButton
          onClick={handleClick}
          sx={{
            transition: 'transform 0.3s ease',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'transparent',
              transform: 'scale(1.2)',
              '& .MuiSvgIcon-root': {
                color: 'text.admin',
              },
            },
          }}
        >
          <BuildIcon sx={{ fontSize: 22, color: isMenuOpen ? 'text.highlight' : 'text.primary' }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          zIndex: 1401,
          '& .MuiPopover-paper': {
            width: 'auto',
            minWidth: 240,
            maxWidth: '23vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: 1,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.text.primary}`,
            boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Stack direction="column">
            <List sx={{ padding: 0 }}>
              <DevMenuItem
                title="Approvers Settings"
                icon={<SettingsIcon />}
                color="rgb(238, 160, 44)"
                onClick={() => {
                  handleOpenApproversSettings()
                  handleClose()
                }}
              />

              <DevMenuItem
                title="Gmail Signature"
                icon={<img src={GmailIconRed} className="dev-menu-icon" style={{ height: 22, width: 22 }} />}
                color="rgb(219, 68, 55)"
                onClick={() => {
                  setSignatureStatus('')
                  setIsSignatureOpen(true)
                  handleClose()
                }}
              />

              <DevMenuItem
                title="Time Tracker"
                icon={
                  <Box sx={{ position: 'relative', display: 'flex' }}>
                    <AccessTimeIcon />
                    {isTracking && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: theme.palette.success.main,
                          border: `1px solid ${theme.palette.background.paper}`,
                        }}
                      />
                    )}
                  </Box>
                }
                color="rgb(56, 182, 255)"
                onClick={toggleWidget}
                secondary={isTracking ? `Session: ${formattedDuration}` : undefined}
              />

              <DevMenuItem
                title="Refresh Search"
                icon={isRefreshing ? <CircularProgress size={22} /> : <RestartAltIcon />}
                color="rgb(76, 175, 80)"
                onClick={() => refreshSearch()}
                disabled={isRefreshing}
              />

              <DevMenuItem
                title="Search Health"
                icon={<SearchHealthBadge />}
                color="rgb(105, 107, 255)"
                onClick={() => {}}
              />
            </List>
          </Stack>
        </ClickAwayListener>
      </Popover>

      <ApproverSettingsModal open={isApproverSettingsOpen} onClose={handleCloseApproversSettings} settings={settings} />

      {isSignatureOpen && (
        <GoogleSignatureModal
          isOpen={isSignatureOpen}
          onClose={() => {
            setSignatureStatus('')
            setIsSignatureOpen(false)
          }}
          user={currentUser}
          onUpdateSignature={handleSetSignature}
          generateSignature={generateSignature}
          generateRenderSignature={generateRenderSignature}
          status={signatureStatus}
        />
      )}
    </Box>
  )
}

export default DevNavMenu
